import { useEffect, useMemo, useRef, useState } from "react";
import { ICustomPanelType } from "../../../../responses/customPanelTypes";
import { IPanelGeneratedDrawingDto } from "../../../../responses/panelGeneratedDrawingDto";
import { IPanelGeneratedModelDto, PanelTypeGenerationStatus } from "../../../../responses/panelGeneratedModelDto";
import { CladdingCellStyle } from "../../../../responses/panelSource";
import { PanelEditorScope } from "../../panels/panel-editor-scope";
import { createEditor2DLouvreSystemSettings } from "../../panels/panelLouvreSourceDefinitionFactory";
import { createEditor2DSystemSettings } from "../../panels/panelWindowSourceDefinitionFactory";
import { PanelEditor2D } from "./2d/panelEditor2D";
import { Editor2DMenu, Tab, TabsVisibility } from "./editor2DMenu";
import { GeneratedPanelDrawings } from "./generatedPanelDrawings";
import { GeneratedPanelModel } from "./generatedPanelModel";
import type { SystemSettings } from "../../../../responses/systemSettings";
import { GeneratedPanelModelLoadedPayload } from "../../eventBus/generatedModelsEventPayloads";
import { ActivatePanelHooksDesignerEventPayload } from "../../eventBus/hooksDesignerEventPayloads";
import { DrawingsLoadingStateEventPayload } from "../../eventBus/typesManagerEventPayloads";
import eventBus, { IApplicationEvent } from "../../eventBus/eventDispatcher";
import "./editor2DContent.css";

type Props = {
    systemSetting: SystemSettings;
    panelEditorScope: PanelEditorScope;
};

export const Panel2DEditorDockingPanelContent = ({ systemSetting, panelEditorScope }: Props) => {
    const [panel, setPanel] = useState<ActivatePanelHooksDesignerEventPayload>();
    const [style, setStyle] = useState<CladdingCellStyle>(CladdingCellStyle.Exposed);
    const windowSettings = useMemo(() => createEditor2DSystemSettings(systemSetting, style), [systemSetting, style]);
    const louvreSettings = useMemo(
        () => createEditor2DLouvreSystemSettings(systemSetting, style),
        [systemSetting, style],
    );
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [areDrawingsLoading, setAreDrawingsLoading] = useState<boolean>(false);
    const [model, setModel] = useState<Omit<IPanelGeneratedModelDto, "typeId">>();
    const [customModelBubbleUrl, setCustomModelBubbleUrl] = useState<string | null>();
    const [selectedTab, setSelectedTab] = useState<Tab>("editor");
    const [drawings, setDrawings] = useState<IPanelGeneratedDrawingDto[]>([]);
    const [selectedDrawingIndex, setSelectedDrawingIndex] = useState(0);
    const panelId = useRef<string>();
    const panelTypeId = useRef<string>();
    const customPanelType = useRef<ICustomPanelType>();

    useEffect(() => {
        const onPanelSelected = (event: IApplicationEvent<ActivatePanelHooksDesignerEventPayload>) => {
            setPanel(event.payload);

            const {
                panel: {
                    id: selectedPanelId,
                    panelTypeId: selectedPanelTypeId,
                    customPanelTypeId: selectedCustomPanelTypeId,
                    style: selectedStyle,
                },
                customPanelType: selectedCustomPanelType,
            } = event.payload;
            setStyle(selectedStyle);

            if (
                panelId.current !== selectedPanelId ||
                panelTypeId.current !== selectedPanelTypeId ||
                customPanelType.current?.id !== selectedCustomPanelTypeId
            ) {
                setSelectedDrawingIndex(0);
            }

            if (panelTypeId.current !== selectedPanelTypeId) {
                setIsLoading(true);
            }

            if (selectedCustomPanelType) {
                setSelectedTab("model");
            } else if (panelTypeId.current !== selectedPanelTypeId) {
                setSelectedTab("editor");
            }

            panelId.current = selectedPanelId;
            panelTypeId.current = selectedPanelTypeId;
            customPanelType.current = selectedCustomPanelType;

            setCustomModelBubbleUrl(selectedCustomPanelType?.bubbleUrl);
        };

        const onLoaded = (event: IApplicationEvent<GeneratedPanelModelLoadedPayload>) => {
            if (panelTypeId.current !== event.payload.panelTypeId) {
                return;
            }

            setIsLoading(false);
            setModel(event.payload.model);
            setDrawings(event.payload.drawings);
        };

        const onDrawingsLoadingStateChanged = (event: IApplicationEvent<DrawingsLoadingStateEventPayload>) => {
            if (panelTypeId.current !== event.payload.panelTypeId) {
                return;
            }

            setAreDrawingsLoading(event.payload.isLoading);
        };

        eventBus.addEventListener("Dextall.Hooks.Designer.Panel.SelectionChanged", onPanelSelected);
        eventBus.addEventListener("Dextall.TypesManager.UI.PanelType.SetGeneratedModel", onLoaded);
        eventBus.addEventListener("Dextall.TypesManager.UI.DrawingsLoadingStateChanged", onDrawingsLoadingStateChanged);

        return () => {
            eventBus.removeEventListener("Dextall.Hooks.Designer.Panel.SelectionChanged", onPanelSelected);
            eventBus.removeEventListener("Dextall.TypesManager.UI.PanelType.SetGeneratedModel", onLoaded);
            eventBus.removeEventListener("Dextall.TypesManager.UI.DrawingsLoadingStateChanged", onDrawingsLoadingStateChanged);
        };
    }, []);

    if (!panel) {
        return null;
    }

    const tabsVisibility: TabsVisibility = {
        model: true,
        editor: !customModelBubbleUrl,
        drawings: !customModelBubbleUrl,
    };

    return (
        <div className="editor-2d-container">
            <Editor2DMenu selectedTab={selectedTab} setSelectedTab={setSelectedTab} tabVisibility={tabsVisibility} />
            <div className="editor-2d-content">
                {selectedTab === "editor" && panelEditorScope.panelEditorContent && (
                    <PanelEditor2D
                        {...panelEditorScope.panelEditorContent}
                        windowSettings={windowSettings}
                        louvreSettings={louvreSettings}
                    />
                )}
                {selectedTab === "model" && !customModelBubbleUrl && (
                    <GeneratedPanelModel
                        model={model}
                        isLoading={isLoading}
                        contentType="panel"
                        panelTypeId={panel.panel.panelTypeId}
                    />
                )}
                {selectedTab === "model" && customModelBubbleUrl && panel.panel.customPanelTypeId && (
                    <GeneratedPanelModel
                        model={{
                            bubble: customModelBubbleUrl,
                            id: panel.panel.customPanelTypeId,
                            status: PanelTypeGenerationStatus.ModelGeneratedSuccessfully,
                        }}
                        contentType="custom-panel"
                        isLoading={false}
                        panelTypeId={panel.panel.customPanelTypeId}
                    />
                )}
                {selectedTab === "drawing" && (
                    <GeneratedPanelDrawings
                        model={model}
                        drawings={drawings}
                        isLoading={isLoading || areDrawingsLoading}
                        selectedDrawingIndex={selectedDrawingIndex}
                        setSelectedDrawingIndex={setSelectedDrawingIndex}
                        panelTypeId={panel.panel.panelTypeId}
                        isPanelModel={true}
                    />
                )}
            </div>
        </div>
    );
};
